import { SAVE_SCHEMA_VERSION, STORAGE_KEYS } from "./config.js";
import {
  clone,
  hydrateRuntime,
  serializeRuntime,
  validateSave,
} from "./core.js";

const DATABASE_NAME = "weatherRealmfall";
const DATABASE_VERSION = 3;
const STORES = [
  "realmfallProfile",
  "realmfallCharacters",
  "realmfallSaveSlots",
  "realmfallStatistics",
  "realmfallAchievements",
  "realmfallBestiary",
  "realmfallItemLibrary",
];

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error("IndexedDB request timed out.")),
      1500,
    );
    request.onsuccess = () => {
      clearTimeout(timeout);
      resolve(request.result);
    };
    request.onerror = () => {
      clearTimeout(timeout);
      reject(request.error ?? new Error("IndexedDB request failed."));
    };
    request.onblocked = () => {
      clearTimeout(timeout);
      reject(new Error("IndexedDB upgrade was blocked."));
    };
  });
}

function transactionPromise(transaction) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error("IndexedDB transaction timed out.")),
      2000,
    );
    transaction.oncomplete = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    transaction.onabort = transaction.onerror = () => {
      clearTimeout(timeout);
      reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    };
  });
}

export class IndexedDBStorage {
  constructor() {
    this.database = null;
    this.available = typeof indexedDB !== "undefined";
  }
  async open() {
    if (!this.available) return null;
    if (this.database) return this.database;
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      for (const store of STORES)
        if (!database.objectStoreNames.contains(store))
          database.createObjectStore(store);
    };
    this.database = await requestPromise(request);
    this.database.onversionchange = () => {
      this.database.close();
      this.database = null;
    };
    return this.database;
  }
  async get(store, key) {
    const database = await this.open();
    if (!database) return null;
    return requestPromise(
      database.transaction(store, "readonly").objectStore(store).get(key),
    );
  }
  async set(store, key, value) {
    const database = await this.open();
    if (!database) return false;
    const transaction = database.transaction(store, "readwrite");
    transaction.objectStore(store).put(clone(value), key);
    await transactionPromise(transaction);
    return true;
  }
  async delete(store, key) {
    const database = await this.open();
    if (!database) return false;
    const transaction = database.transaction(store, "readwrite");
    transaction.objectStore(store).delete(key);
    await transactionPromise(transaction);
    return true;
  }
  close() {
    this.database?.close();
    this.database = null;
  }
}

export function defaultSettings() {
  const mobile =
    typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const reducedMotion =
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;
  return {
    schemaVersion: 1,
    quality: mobile ? "low" : "medium",
    masterVolume: 0.7,
    musicVolume: 0.45,
    effectsVolume: 0.7,
    reducedMotion,
    screenShake: !reducedMotion,
    reduceFlashes: reducedMotion,
    fixedCamera: false,
    largeCursor: false,
    largeHud: false,
    highContrast: false,
    cameraZoom: 24,
  };
}

export function validateSettings(value) {
  const defaults = defaultSettings();
  if (!value || typeof value !== "object") return defaults;
  return {
    ...defaults,
    quality: ["low", "medium", "high", "ultra"].includes(value.quality)
      ? value.quality
      : defaults.quality,
    masterVolume: Number.isFinite(value.masterVolume)
      ? Math.max(0, Math.min(1, value.masterVolume))
      : defaults.masterVolume,
    musicVolume: Number.isFinite(value.musicVolume)
      ? Math.max(0, Math.min(1, value.musicVolume))
      : defaults.musicVolume,
    effectsVolume: Number.isFinite(value.effectsVolume)
      ? Math.max(0, Math.min(1, value.effectsVolume))
      : defaults.effectsVolume,
    reducedMotion: Boolean(value.reducedMotion),
    screenShake: Boolean(value.screenShake),
    reduceFlashes: Boolean(value.reduceFlashes),
    fixedCamera: Boolean(value.fixedCamera),
    largeCursor: Boolean(value.largeCursor),
    largeHud: Boolean(value.largeHud),
    highContrast: Boolean(value.highContrast),
    cameraZoom: Number.isFinite(value.cameraZoom)
      ? Math.max(14, Math.min(34, value.cameraZoom))
      : defaults.cameraZoom,
  };
}

export class SaveManager {
  constructor(storage = new IndexedDBStorage()) {
    this.storage = storage;
    this.settings = defaultSettings();
    this.slots = [null, null, null];
    this.saveInFlight = null;
  }
  async initialize() {
    try {
      this.settings = validateSettings(
        JSON.parse(localStorage.getItem(STORAGE_KEYS.settings) || "null"),
      );
    } catch (_error) {
      this.settings = defaultSettings();
    }
    try {
      const stored = await this.storage.get("realmfallSaveSlots", "slots");
      if (Array.isArray(stored))
        this.slots = stored
          .slice(0, 3)
          .concat([null, null, null])
          .slice(0, 3)
          .map((slot) => this.migrate(slot));
      else this.loadFallback();
    } catch (_error) {
      this.loadFallback();
    }
    return this;
  }
  loadFallback() {
    try {
      const stored = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.fallback) || "null",
      );
      if (Array.isArray(stored))
        this.slots = stored
          .slice(0, 3)
          .concat([null, null, null])
          .slice(0, 3)
          .map((slot) => this.migrate(slot));
    } catch (_error) {
      this.slots = [null, null, null];
    }
  }
  migrate(slot) {
    if (!slot) return null;
    const candidate = clone(slot);
    if (candidate.schemaVersion === 2) {
      candidate.schemaVersion = SAVE_SCHEMA_VERSION;
      candidate.character.achievements ??= [];
      candidate.character.itemLibrary ??= [];
      candidate.paused = true;
      candidate.gameState = "paused";
    }
    if (candidate.schemaVersion === SAVE_SCHEMA_VERSION) {
      candidate.character.inventory ??= [];
      candidate.character.stash ??= [];
      candidate.character.defeatedBosses ??= [];
      candidate.character.campaignChoices ??= {};
      candidate.character.endings ??= [];
      candidate.summons ??= [];
      candidate.projectiles ??= [];
      candidate.effects ??= [];
      candidate.corpses ??= [];
      candidate.groundItems ??= [];
      candidate.player.path ??= [];
      candidate.zoneInitialized ??= Array.isArray(candidate.interactables);
      candidate.paused = true;
      candidate.gameState = "paused";
    }
    return validateSave(candidate).valid ? candidate : null;
  }
  persistFallback(slots = this.slots) {
    try {
      localStorage.setItem(STORAGE_KEYS.fallback, JSON.stringify(slots));
      return true;
    } catch (_error) {
      return false;
    }
  }
  saveSettings(settings) {
    this.settings = validateSettings(settings);
    try {
      localStorage.setItem(
        STORAGE_KEYS.settings,
        JSON.stringify(this.settings),
      );
    } catch (_error) {
      /* Settings persistence is best effort. */
    }
  }
  firstFreeSlot() {
    return this.slots.findIndex((entry) => !entry);
  }
  async save(index, runtime) {
    if (!Number.isInteger(index) || index < 0 || index >= 3)
      throw new Error("Invalid save slot.");
    if (this.saveInFlight) await this.saveInFlight;
    const snapshot = serializeRuntime(runtime);
    const validation = validateSave(snapshot);
    if (!validation.valid) throw new Error(validation.reason);
    const previous = this.slots[index] ? clone(this.slots[index]) : null;
    this.slots[index] = snapshot;
    this.saveInFlight = (async () => {
      try {
        if (previous)
          await this.storage.set(
            "realmfallSaveSlots",
            `backup-${index}`,
            previous,
          );
        const saved = await this.storage.set(
          "realmfallSaveSlots",
          "slots",
          this.slots,
        );
        if (!saved)
          if (!this.persistFallback())
            throw new Error("No local save storage is available.");
        await this.storage.set(
          "realmfallCharacters",
          snapshot.character.id,
          snapshot.character,
        );
        await this.storage.set(
          "realmfallStatistics",
          snapshot.character.id,
          snapshot.character.statistics,
        );
        await this.storage.set(
          "realmfallAchievements",
          snapshot.character.id,
          snapshot.character.achievements,
        );
        await this.storage.set(
          "realmfallBestiary",
          snapshot.character.id,
          snapshot.character.bestiary,
        );
        await this.storage.set(
          "realmfallItemLibrary",
          snapshot.character.id,
          snapshot.character.itemLibrary,
        );
      } catch (_error) {
        if (!this.persistFallback())
          throw new Error("Realmfall could not persist this save.");
      } finally {
        this.saveInFlight = null;
      }
    })();
    await this.saveInFlight;
    return snapshot;
  }
  load(index) {
    const slot = this.slots[index];
    if (!slot) throw new Error("Save slot is empty.");
    return hydrateRuntime(slot);
  }
  async delete(index) {
    if (!Number.isInteger(index) || index < 0 || index >= 3) return false;
    this.slots[index] = null;
    try {
      await this.storage.set("realmfallSaveSlots", "slots", this.slots);
    } catch (_error) {
      if (!this.persistFallback()) return false;
    }
    return true;
  }
  export(index) {
    const slot = this.slots[index];
    if (!slot) throw new Error("Save slot is empty.");
    return JSON.stringify(
      {
        game: "Realmfall: Shattered Crown",
        exportedAt: new Date().toISOString(),
        save: slot,
      },
      null,
      2,
    );
  }
  async import(text) {
    if (String(text).length > 2_000_000)
      throw new Error("Imported save is too large.");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (_error) {
      throw new Error("Imported file is not valid JSON.");
    }
    const migrated = this.migrate(parsed?.save ?? parsed);
    if (!migrated) throw new Error("Imported save failed validation.");
    const index = this.firstFreeSlot();
    if (index < 0) throw new Error("All three save slots are occupied.");
    const nextSlots = this.slots.slice();
    nextSlots[index] = clone(migrated);
    try {
      const saved = await this.storage.set(
        "realmfallSaveSlots",
        "slots",
        nextSlots,
      );
      if (!saved && !this.persistFallback(nextSlots))
        throw new Error("No local save storage is available.");
    } catch (error) {
      if (!this.persistFallback(nextSlots)) throw error;
    }
    this.slots = nextSlots;
    return index;
  }
  teardown() {
    this.storage.close();
  }
}

export { DATABASE_NAME, DATABASE_VERSION, STORES };
